import { forwardRef, useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import SelectInfinite from 'Components/Molecules/SelectInfinite/SelectInfinite';
import { getInvoice } from 'Services/invoices';
import { trackError } from 'Utils/errorMonitoring';
import useInfiniteInvoices from 'Hooks/useInfiniteInvoices';
import { formatCurrency } from 'Utils/formatters';
import useLocale from 'Hooks/useLocale';

const SelectInvoiceLabel = (props) => {
    const { identifier, amount, currency, dateEmission } = props;
    const locale = useLocale();

    return (
        <div className="flex items-center gap-1.5">
            <div className="truncate grow">{identifier}</div>
            <div className="opacity-50 text-xs">{dateEmission}</div>
            <div className="opacity-50 text-xs min-w-[62px] text-right">
                {formatCurrency(amount, { currency: currency, locale })}
            </div>
        </div>
    );
};

SelectInvoiceLabel.propTypes = {
    identifier: PropTypes.string,
    currency: PropTypes.string,
    amount: PropTypes.number,
    dateEmission: PropTypes.string,
};

const SelectInvoice = forwardRef(function SelectInvoice(props, ref) {
    const { className, label, value, onChange, error, disabled, filters, ...rest } = props;
    const [displayValue, setDisplayValue] = useState('');
    const [fetchedContact, setFetchedContact] = useState(null);

    const [query, setQuery] = useState('');
    const { data, isFetching, fetchNextPage, hasNextPage } = useInfiniteInvoices({ query, ...filters });

    const items = useMemo(() => {
        return (
            data?.pages?.reduce((acc, page) => {
                return [...acc, ...page.items];
            }, []) ?? []
        );
    }, [data]);

    const options = useMemo(() => {
        return items.map((item) => ({
            id: item.id,
            label: (
                <SelectInvoiceLabel
                    key={item.id}
                    identifier={item.identifier ?? '-'}
                    amount={item.amount ?? 0}
                    currency={item.currency ?? 'EUR'}
                    dateEmission={item.date_emission ? item.date_emission.toLocaleDateString() : '-'}
                />
            ),
        }));
    }, [items]);

    const handleChange = (id) => {
        if (!id) {
            onChange(id, null);
            return;
        }
        const fromPages = items.find((item) => item.id === id);
        const invoice = fromPages || (fetchedContact?.id === id ? fetchedContact : null);
        onChange(id, invoice);
    };

    useEffect(() => {
        if (query) return;

        if (!value || options.length === 0) {
            setDisplayValue('');
            setFetchedContact(null);
            return;
        }

        const matchedOption = options.find((option) => option.id === value);
        if (matchedOption) {
            setDisplayValue(matchedOption.label);
            setFetchedContact(null);
        } else if (!fetchedContact || fetchedContact.id !== value) {
            setDisplayValue('');
            getInvoice(value)
                .then((invoice) => {
                    if (invoice) {
                        setDisplayValue(
                            <SelectInvoiceLabel
                                identifier={invoice.identifier ?? '-'}
                                amount={invoice.amount ?? 0}
                                currency={invoice.currency ?? 'EUR'}
                                dateEmission={invoice.date_emission ? invoice.date_emission.toLocaleDateString() : '-'}
                            />,
                        );
                        setFetchedContact(invoice);
                    }
                })
                .catch((error) => {
                    trackError(error);
                });
        }
    }, [value, isFetching, query]);

    return (
        <SelectInfinite
            disabled={disabled}
            ref={ref}
            floatLabel={label}
            label={displayValue}
            value={value}
            onChange={handleChange}
            options={options}
            query={query}
            onChangeQuery={setQuery}
            isFetching={isFetching}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            className={className}
            enableClear={!!displayValue}
            error={error}
            onClear={() => {
                onChange(null, null);
                setQuery('');
            }}
            {...rest}
        />
    );
});

export default SelectInvoice;

SelectInvoice.propTypes = {
    className: PropTypes.string,
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    disabled: PropTypes.bool,
    filters: PropTypes.object,
};
